import { useQuery } from '@tanstack/react-query';

import { AGENT_CATEGORY_CHIP_COLORS } from '@/shared/constants/category-colors';
import { getActiveWorkspaceId } from '@/lib/workspace-context/workspace-context-bindings';
import { agentQueryOptions } from '../../api/agents-fetch.api';

/**
 * The chip color for an agent's Tools Hub category, read from the agent rather than assumed.
 *
 * Widget headers used to hardcode a color name, which only held while the agent stayed in the
 * category it shipped in. An admin can now move it per organization or workspace, so the color
 * has to come from where the tile and the avatar get theirs.
 *
 * Returns `undefined` while the agent is loading and for an agent in no category, so the chip
 * falls back to its own default rather than asserting a category we do not have yet.
 *
 * The workspace comes from the binding seam rather than `@/entities/workspace`, which an entity
 * may not import at runtime; it is the same seam, and the same resulting cache key, that
 * `fetchAgent` uses for this query.
 */
export function useAgentCategoryChipColor(agentId: string) {
  const { data: agent } = useQuery({
    ...agentQueryOptions(agentId, getActiveWorkspaceId()),
    enabled: !!agentId,
  });

  return agent?.tag ? AGENT_CATEGORY_CHIP_COLORS[agent.tag] : undefined;
}
